import { isPlatformBrowser } from "@angular/common";
import { AfterViewInit, Component, Inject, OnInit, PLATFORM_ID, ViewChild } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { SetBreadcrumb } from "@app/store/actions/breadcrumb.actions";
import { MainState } from "@app/store/state";
import { UserProfileInterface, PreferredForumFeedType } from "@core/interfaces/user-profile.interface";
import { AuthService } from "@core/services/auth.service";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { DeviceService } from "@core/services/device.service";
import { ImageService } from "@core/services/image/image.service";
import { ImageViewerService } from "@core/services/image-viewer.service";
import { TitleService } from "@core/services/title/title.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { UpdateUserProfile } from "@features/account/store/auth.actions";
import { ForumTabType } from "@features/forum/forum-tab-type.enum";
import { FeedComponent } from "@features/home/components/feed/feed.component";
import { IotdComponent } from "@features/home/components/iotd/iotd.component";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { ForumPreviewComponent } from "@shared/components/forums/forum-preview/forum-preview.component";
import { filter, pairwise, takeUntil } from "rxjs/operators";

@Component({
  selector: "astrobin-home",
  template: `
    <div class="page">
      <astrobin-swipe-to-refresh (swipe)="onSwipeToRefresh()"></astrobin-swipe-to-refresh>

      <astrobin-upgrade-nudge></astrobin-upgrade-nudge>

      <astrobin-iotd #iotd></astrobin-iotd>

      <div *ngIf="!isPwa" class="latest-from-the-forums">
        <div
          class="
          header
          d-flex
          justify-content-center justify-content-md-between
          align-items-center
          flex-column flex-md-row
          gap-3 gap-sm-2
          mb-3
        "
        >
          <div
            *ngIf="
              ((currentUserProfile$ | async) === null ||
                (currentUserProfile$ | async).enableNewForumExperience !== false ||
                isPwa) &&
                selectedFeedType;
              else defaultForumHeader
            "
            class="d-inline-block"
            ngbDropdown
          >
            <h4 class="mb-0">
              <button
                id="homeFeedTypeDropdown"
                class="btn btn-link dropdown-toggle p-0 text-decoration-none h4-button"
                ngbDropdownToggle
                type="button"
              >
                {{ getCurrentFeedLabel() }}
              </button>
            </h4>
            <div class="dropdown-menu" aria-labelledby="homeFeedTypeDropdown" ngbDropdownMenu>
              <button
                *ngIf="isAuthenticated"
                [class.active]="selectedFeedType === ForumTabType.MY_FEED"
                (click)="changeFeedType(ForumTabType.MY_FEED)"
                class="dropdown-item"
                type="button"
              >
                <fa-icon [icon]="['fas', 'rss']" class="me-2"></fa-icon>
                <div>
                  <div>{{ "My forum feed" | translate }}</div>
                  <small class="text-muted">{{ "A curated list of topics that you can customize." | translate }}</small>
                </div>
              </button>
              <button
                [class.active]="selectedFeedType === ForumTabType.ACTIVE"
                (click)="changeFeedType(ForumTabType.ACTIVE)"
                class="dropdown-item"
                type="button"
              >
                <fa-icon [icon]="['fas', 'bolt']" class="me-2"></fa-icon>
                <div>
                  <div>{{ "Active forum topics" | translate }}</div>
                  <small class="text-muted">{{ "Topics with a high activity at the moment." | translate }}</small>
                </div>
              </button>
              <button
                [class.active]="selectedFeedType === ForumTabType.NEW"
                (click)="changeFeedType(ForumTabType.NEW)"
                class="dropdown-item"
                type="button"
              >
                <fa-icon [icon]="['fas', 'star']" class="me-2"></fa-icon>
                <div>
                  <div>{{ "New forum topics" | translate }}</div>
                  <small class="text-muted">{{ "Topics that were recently started." | translate }}</small>
                </div>
              </button>
              <button
                [class.active]="selectedFeedType === ForumTabType.LATEST"
                (click)="changeFeedType(ForumTabType.LATEST)"
                class="dropdown-item"
                type="button"
              >
                <fa-icon [icon]="['fas', 'clock']" class="me-2"></fa-icon>
                <div>
                  <div>{{ "Latest from the forums" | translate }}</div>
                  <small class="text-muted">{{ "Topics with recent replies." | translate }}</small>
                </div>
              </button>
            </div>
          </div>
          <ng-template #defaultForumHeader>
            <h4 class="mb-0">{{ "Latest from the forums" | translate }}</h4>
          </ng-template>
          <div class="d-flex gap-2 align-items-center">
            <a
              *ngIf="
                (currentUserProfile$ | async) === null ||
                  (currentUserProfile$ | async).enableNewForumExperience !== false ||
                  isPwa;
                else classicForumLink
              "
              [fragment]="(currentUserProfile$ | async)?.preferredForumFeedType || 'LATEST'"
              [routerLink]="['/forum']"
              class="no-wrap btn btn-link link-primary btn-xs m-0"
            >
              {{ "View all" | translate }}
            </a>
            <ng-template #classicForumLink>
              <a [href]="classicRoutesService.FORUM_LATEST" class="no-wrap btn btn-link link-primary btn-xs m-0">
                {{ "View all" | translate }}
              </a>
            </ng-template>

            <a
              *ngIf="
                (currentUserProfile$ | async) === null ||
                  (currentUserProfile$ | async).enableNewForumExperience !== false ||
                  isPwa;
                else classicNewTopicLink
              "
              [routerLink]="['/forum/new-topic']"
              class="no-wrap btn btn-primary btn-xs"
            >
              {{ "New topic" | translate }}
            </a>
            <ng-template #classicNewTopicLink>
              <a [href]="classicRoutesService.FORUM_HOME + '#new-topic'" class="no-wrap btn btn-primary btn-xs">
                {{ "New topic" | translate }}
              </a>
            </ng-template>
          </div>
        </div>

        <astrobin-loading-indicator *ngIf="!isBrowser"></astrobin-loading-indicator>
        <astrobin-forum-preview
          #forumPreview
          *ngIf="isBrowser"
          [initialFeedType]="selectedFeedType"
          [showFooter]="false"
          [showHeader]="false"
          [useCard]="false"
        ></astrobin-forum-preview>
      </div>

      <astrobin-feed #feed *ngIf="isBrowser"></astrobin-feed>
    </div>
  `,
  styleUrls: ["./home.component.scss"]
})
export class HomeComponent extends BaseComponentDirective implements OnInit, AfterViewInit {
  @ViewChild("iotd") iotdComponent: IotdComponent;
  @ViewChild("forumPreview") forumPreviewComponent: ForumPreviewComponent;
  @ViewChild("feed") feedComponent: FeedComponent;
  readonly ForumTabType = ForumTabType;
  protected isBrowser: boolean;
  isAuthenticated = false;
  currentUserProfile: UserProfileInterface | null = null;
  selectedFeedType: PreferredForumFeedType;
  isPwa: boolean = false;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly titleService: TitleService,
    public readonly translateService: TranslateService,
    public readonly classicRoutesService: ClassicRoutesService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    public readonly windowRefService: WindowRefService,
    public readonly router: Router,
    public readonly activatedRoute: ActivatedRoute,
    public readonly imageService: ImageService,
    public readonly imageViewerService: ImageViewerService,
    private readonly authService: AuthService,
    public readonly deviceService: DeviceService
  ) {
    super(store$);
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isPwa = this.deviceService.isPwa();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        pairwise(),
        takeUntil(this.destroyed$)
      )
      .subscribe(([prev, current]: [NavigationEnd, NavigationEnd]) => {
        const isBack = this.router.getCurrentNavigation()?.trigger === "popstate";
        const isSameUrl = prev.urlAfterRedirects === current.urlAfterRedirects;
        if (this.isBrowser && isSameUrl && !isBack) {
          this.windowRefService.nativeWindow.location.reload();
        }
      });
  }

  ngOnInit() {
    super.ngOnInit();

    this.store$.dispatch(new SetBreadcrumb({ breadcrumb: [] }));

    if (this.activatedRoute.snapshot.data.image) {
      this.imageService.setMetaTags(this.activatedRoute.snapshot.data.image);
    } else {
      this.titleService.setTitle(this.translateService.instant("Home of Astrophotography"));
    }

    // Initialize authentication and user profile
    this.authService
      .isAuthenticated$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(isAuthenticated => {
        this.isAuthenticated = isAuthenticated;
      });

    this.currentUserProfile$.pipe(takeUntil(this.destroyed$)).subscribe((profile: UserProfileInterface | null) => {
      this.currentUserProfile = profile;
      this._initializeFeedType();
    });
  }

  ngAfterViewInit() {
    this.imageViewerService.autoOpenSlideshow(this.componentId, this.activatedRoute);
  }

  onSwipeToRefresh(): void {
    if (!this.isBrowser) {
      return;
    }

    // Refresh all components - each has its own loading indicator
    if (this.iotdComponent) {
      this.iotdComponent.refresh();
    }

    if (this.forumPreviewComponent) {
      this.forumPreviewComponent.refresh();
    }

    if (this.feedComponent) {
      this.feedComponent.refresh();
    }
  }

  /**
   * Initialize the selected feed type based on user preference or defaults
   */
  private _initializeFeedType(): void {
    if (this.isAuthenticated) {
      // Use user preference or default to MY_FEED for authenticated users
      this.selectedFeedType = this.currentUserProfile?.preferredForumFeedType || ForumTabType.MY_FEED;
    } else {
      // Default to ACTIVE for anonymous users
      this.selectedFeedType = ForumTabType.ACTIVE;
    }
  }

  /**
   * Check if a feed type is valid for saving preferences
   */
  private _isValidFeedType(feedType: ForumTabType): boolean {
    return [ForumTabType.MY_FEED, ForumTabType.ACTIVE, ForumTabType.LATEST, ForumTabType.NEW].includes(feedType);
  }

  /**
   * Change the selected feed type and update user preferences
   */
  changeFeedType(feedType: PreferredForumFeedType): void {
    this.selectedFeedType = feedType;

    // Save preference if authenticated and this is a valid feed type
    if (this.isAuthenticated && this.currentUserProfile && this._isValidFeedType(feedType)) {
      this.store$.dispatch(
        new UpdateUserProfile({
          id: this.currentUserProfile.id,
          preferredForumFeedType: feedType
        })
      );
    }

    // Update the forum preview component's feed type and refresh
    if (this.forumPreviewComponent) {
      this.forumPreviewComponent.changeFeedType(feedType);
    }
  }

  /**
   * Get the icon for the current selected feed type
   */
  getCurrentFeedIcon(): IconProp {
    switch (this.selectedFeedType) {
      case ForumTabType.MY_FEED:
        return ["fas", "rss"];
      case ForumTabType.ACTIVE:
        return ["fas", "bolt"];
      case ForumTabType.NEW:
        return ["fas", "star"];
      case ForumTabType.LATEST:
        return ["fas", "clock"];
      default:
        return ["fas", "rss"]; // Default to my feed
    }
  }

  /**
   * Get the label for the current selected feed type
   */
  getCurrentFeedLabel(): string {
    switch (this.selectedFeedType) {
      case ForumTabType.MY_FEED:
        return this.translateService.instant("My forum feed");
      case ForumTabType.ACTIVE:
        return this.translateService.instant("Active forum topics");
      case ForumTabType.NEW:
        return this.translateService.instant("New forum topics");
      case ForumTabType.LATEST:
        return this.translateService.instant("Latest from the forums");
      default:
        return this.translateService.instant("My forum feed"); // Default to my forum feed
    }
  }

  /**
   * Get the subtitle for the current selected feed type
   */
  getCurrentFeedSubtitle(): string {
    switch (this.selectedFeedType) {
      case ForumTabType.MY_FEED:
        return this.translateService.instant("A curated list of topics that you can customize.");
      case ForumTabType.ACTIVE:
        return this.translateService.instant("Topics with a high activity at the moment.");
      case ForumTabType.NEW:
        return this.translateService.instant("Topics that were recently started.");
      case ForumTabType.LATEST:
        return this.translateService.instant("Topics with recent replies.");
      default:
        return this.translateService.instant("Latest from the forums");
    }
  }
}
